import {
  LocalizationProvider,
  DatePicker,
  TimePicker,
} from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { FC } from "react";
import { Colors } from "../../../configs";
import dayjs, { Dayjs } from "dayjs";
import { pxToRem } from "../../../util";

dayjs.extend(utc);
dayjs.extend(timezone);

export interface ICustomDateTimePickerProps {
  type: "date" | "time";
  value: Dayjs | null;
  onChange: (date: Dayjs | null) => void;
  disabled?: boolean;
  sx?: any;
  name?: string;
  enableSelect?: "past" | "future" | "all";
  view?: "day" | "year" | "month";
}

const CustomDateTimePicker: FC<ICustomDateTimePickerProps> = ({
  type,
  value,
  onChange,
  disabled = false,
  sx = {},
  name,
  enableSelect = "future",
  view,
}) => {
  const safeValue: Dayjs | null = value
    ? dayjs.isDayjs(value)
      ? value
      : dayjs(value)
    : null;

  const today = dayjs();

  const pickerStyle = {
    padding: 1,
    width: "9.35714286rem",
    height: "2.6rem",
    borderRadius: "0.57142857rem",
    "& input": {
      padding: 0,
      width: "9.35714286rem",
      height: "2.6rem",
      px: "1rem",
      py: "0.214285714rem",
      boxSizing: "border-box",
      fontSize: "1.05rem",
      lineHeight: "1.2rem",
      fontWeight: 500,
      letterSpacing: "0.04rem",
    },
    "& button": {
      padding: "0.642857143rem",
    },
  } as const;

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      {type === "date" ? (
        <DatePicker
          format={view === "month" ? "MM/YYYY" : "MM/DD/YYYY"}
          sx={{
            pickerStyle,
            backgroundColor: Colors.WHITE,
            borderRadius: "0.57142857rem",
            "& input": { py: pxToRem(7.5) },
            ...sx,
          }}
          value={safeValue}
          onChange={(v) =>
            onChange(v ? (dayjs.isDayjs(v) ? v : dayjs(v)) : null)
          }
          minDate={
            enableSelect === "past" || enableSelect === "all"
              ? undefined
              : today
          }
          maxDate={
            enableSelect === "future" || enableSelect === "all"
              ? undefined
              : today
          }
          orientation="portrait"
          views={view === "month" ? ["year", "month"] : view ? [view] : undefined}
          openTo={view === "month" ? "month" : view ?? "day"}
          slotProps={{
            layout: {
              sx: {
                width: "100%",
                height: "100%",
                padding: 1,
                cursor: "pointer",
                ".MuiPickersDay-root": {
                  fontSize: "1.35rem",
                  padding: 2.5,
                },
                ".MuiPickersLayout-root": {
                  width: "100%",
                  height: "100%",
                },
                ".MuiPickersCalendarHeader-label": {
                  fontSize: "1.5rem",
                  ml: -0.1,
                },
                ".MuiYearCalendar-root": {
                  maxHeight: "332px",
                  overflowY: "auto",
                },
                ".MuiPickersCalendarHeader-switchViewIcon": {
                  fontSize: "2.8rem",
                  marginTop: "-0.314285714rem",
                },
                ".MuiDayCalendar-weekDayLabel": {
                  fontSize: "1.25rem",
                  paddingX: 2.5,
                },
                ".MuiSvgIcon-root.MuiSvgIcon-fontSizeInherit.css-1vooibu-MuiSvgIcon-root":
                  {
                    height: "2.3rem",
                    width: "2.3rem",
                  },
                ".MuiPickersMonth-monthButton": {
                  fontSize: "1.2rem",
                },
                ".MuiPickersYear-yearButton": {
                  fontSize: "1.2rem",
                },
              },
            },
          }}
          disabled={disabled}
          name={name}
        />
      ) : (
        <TimePicker
          sx={{
            pickerStyle,
            width: "15rem",
            backgroundColor: Colors.WHITE,
            borderRadius: "0.57142857rem",
            "& input": { py: pxToRem(7.5) },
            ...sx,
          }}
          value={safeValue}
          onChange={(v) =>
            onChange(v ? (dayjs.isDayjs(v) ? v : dayjs(v)) : null)
          }
          orientation="portrait"
          slotProps={{
            layout: {
              sx: {
                ".MuiClockNumber-root": {
                  fontSize: "1.35rem",
                },
                ".MuiSvgIcon-root.MuiSvgIcon-fontSizeInherit.css-1vooibu-MuiSvgIcon-root":
                  {
                    height: "2rem",
                    width: "2rem",
                  },
                ".MuiPickersArrowSwitcher-root": {
                  marginTop: "-1rem",
                },
                ".MuiTypography-root.MuiTypography-subtitle2.MuiTimePickerToolbar-ampmLabel.MuiPickersToolbarText-root.css-7kirvq-MuiTypography-root-MuiPickersToolbarText-root":
                  {
                    fontSize: "1.35rem",
                    marginLeft: "0.5rem",
                    letterSpacing: "0.125rem",
                  },
              },
            },
          }}
          disabled={disabled}
          name={name}
        />
      )}
    </LocalizationProvider>
  );
};

export default CustomDateTimePicker;
